import React, { useState, useEffect } from 'react';
import { AppState, BookProject } from './types';
import { Dashboard } from './components/Dashboard';
import { ProjectWizard } from './components/ProjectWizard';
import { LandingPage } from './components/LandingPage';
import { NotificationToast } from './components/NotificationToast';
import { Icons } from './components/Icons';
import * as GeminiService from './services/geminiService';

// --- INITIAL STATE ---
const getInitialState = (): AppState => ({
  projects: [],
  currentProjectId: null,
  viewMode: 'landing', 
  themeMode: 'light',
  activeTab: 'outline',
  activeChapterId: null,
});

const PROJECTS_STORAGE_KEY = 'bookwiz_projects';

// 4. MAIN APP
const App = () => {
  const [appState, setAppState] = useState<AppState>(getInitialState());
  const [notification, setNotification] = useState<{message: string, type: 'error' | 'success'} | null>(null);

  // Load Projects on Mount
  useEffect(() => {
    const initSession = async () => {
      // 1. Recover API Key
      if (GeminiService.hasApiKey() && appState.viewMode === 'landing') {
        const storedKey = GeminiService.getApiKey();
        const isValid = await GeminiService.validateApiKey(storedKey);
        
        if (isValid) {
          // 2. Recover Saved Projects
          const savedProjects = localStorage.getItem(PROJECTS_STORAGE_KEY);
          let loadedProjects: BookProject[] = [];
          
          if (savedProjects) {
            try {
              loadedProjects = JSON.parse(savedProjects);
            } catch (e) {
              console.error("Failed to parse saved projects", e);
            }
          } 
          
          // Also check for legacy single project
          const legacyProject = localStorage.getItem('bookwiz_project');
          if (legacyProject && loadedProjects.length === 0) {
            try {
               const p = JSON.parse(legacyProject);
               loadedProjects = [p];
               localStorage.removeItem('bookwiz_project'); // Migrate
            } catch (e) { console.error(e); }
          }

          if (loadedProjects.length > 0) {
            // Go to projects list
             setAppState(prev => ({ 
               ...prev, 
               viewMode: 'projects', 
               projects: loadedProjects 
             }));
          } else {
            // No projects, go to setup
            setAppState(prev => ({ ...prev, viewMode: 'projects', projects: [] }));
          }
        } else {
          console.warn("Stored API Key is invalid. Resetting.");
          GeminiService.setApiKey(null);
        }
      }
    };
    
    initSession();
  }, []);

  // Save projects whenever they change
  useEffect(() => {
    if (appState.projects.length > 0) {
      localStorage.setItem(PROJECTS_STORAGE_KEY, JSON.stringify(appState.projects));
    }
  }, [appState.projects]);

  const handleError = (msg: string) => {
    // Check for 429 Quota Exceeded error
    const isRateLimit = msg.includes('429') || msg.includes('quota') || msg.includes('RESOURCE_EXHAUSTED') || msg.includes('overloaded');
    
    if (isRateLimit) {
       setNotification({
         type: 'error',
         message: 'Quota Exceeded or Rate Limited. The AI switched to fallback models but still hit a limit. Please check your billing or wait a moment.'
       });
    } else {
       let cleanMessage = msg;
       const jsonMatch = msg.match(/\{.*"error":.*\}/);
       if (jsonMatch) {
         try {
           const errorObj = JSON.parse(jsonMatch[0]);
           if (errorObj.error && errorObj.error.message) {
             cleanMessage = errorObj.error.message;
           } else if (errorObj.message) {
             cleanMessage = errorObj.message;
           }
         } catch (e) {
           try {
              const match = msg.match(/"message":\s*"(.*?)"/);
              if (match && match[1]) cleanMessage = match[1];
           } catch (regexErr) { /* ignore */ }
         }
       } else if (msg.includes('GoogleGenAI Error')) {
          cleanMessage = msg.replace(/\[GoogleGenAI Error\]: /, '');
       }

       setNotification({ type: 'error', message: cleanMessage.slice(0, 200) + (cleanMessage.length > 200 ? '...' : '') });
    }
    setTimeout(() => setNotification(null), 8000);
  };

  const handleSuccess = (msg: string) => {
    setNotification({ message: msg, type: 'success' });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleStart = async (apiKey: string) => {
    const isValid = await GeminiService.validateApiKey(apiKey);

    if (isValid) {
      GeminiService.setApiKey(apiKey);
      handleSuccess("API Key Verified Successfully");
      
      const savedProjects = localStorage.getItem(PROJECTS_STORAGE_KEY);
      let loadedProjects: BookProject[] = [];
      if (savedProjects) {
        try { loadedProjects = JSON.parse(savedProjects); } catch(e) {}
      }

      setAppState(prev => ({ 
        ...prev, 
        projects: loadedProjects,
        viewMode: 'projects' 
      }));
    } else {
      handleError("Invalid API Key. Please check your key and try again.");
    }
  };

  const handleLogout = () => {
    GeminiService.setApiKey(null);
    setAppState(getInitialState());
  };

  // --- PROJECT MANAGEMENT ---
  const handleCreateProject = (newProject: BookProject) => {
    setAppState(prev => ({
      ...prev,
      projects: [...prev.projects, newProject],
      currentProjectId: newProject.id,
      viewMode: 'dashboard',
      activeChapterId: newProject.outline[0]?.id || null
    }));
    handleSuccess("Project created!");
  };

  const handleDeleteProject = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if(window.confirm("Are you sure you want to delete this project? This cannot be undone.")) {
      const remaining = appState.projects.filter(p => p.id !== id);
      setAppState(prev => ({ ...prev, projects: remaining }));
      localStorage.setItem(PROJECTS_STORAGE_KEY, JSON.stringify(remaining));
    }
  };

  const handleSelectProject = (id: string) => {
    const proj = appState.projects.find(p => p.id === id);
    if (proj) {
      setAppState(prev => ({
        ...prev,
        currentProjectId: id,
        viewMode: 'dashboard',
        activeChapterId: proj.outline[0]?.id || null,
        activeTab: 'outline' // Reset tab
      }));
    }
  };

  const updateCurrentProject = (updates: Partial<BookProject>) => {
    setAppState(prev => {
       const updatedProjects = prev.projects.map(p => 
          p.id === prev.currentProjectId ? { ...p, ...updates, lastModified: Date.now() } : p
       );
       return { ...prev, projects: updatedProjects };
    });
  };

  // --- RENDER ---
  const currentProject = appState.projects.find(p => p.id === appState.currentProjectId);

  if (appState.viewMode === 'projects') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
        <nav className="bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center sticky top-0 z-20 shadow-sm h-16">
          <div className="flex items-center gap-3">
             <div className="w-8 h-8 bg-ink text-white rounded-lg flex items-center justify-center shadow-lg">
               <Icons.Book />
             </div>
             <span className="font-serif font-bold text-lg text-ink">BookWiz</span>
          </div>
          <div className="flex items-center gap-4">
             <button 
               onClick={() => setAppState(prev => ({ ...prev, viewMode: 'setup' }))}
               className="flex items-center gap-2 px-3 py-1.5 bg-ink text-white rounded-lg shadow-sm hover:bg-gray-800 transition-all hover:-translate-y-0.5 text-[10px] uppercase font-bold tracking-wider"
             >
               <Icons.Plus className="w-3 h-3" /> New Project
             </button>
             <div className="h-4 w-px bg-gray-200"></div>
             <button onClick={handleLogout} className="text-[10px] font-bold uppercase tracking-wider text-gray-400 hover:text-red-600 transition-colors">Logout</button>
          </div>
        </nav>
        
        <main className="flex-1 max-w-6xl w-full mx-auto p-8 animate-fade-in-up">
           <div className="mb-8">
              <h1 className="text-3xl font-serif font-bold text-ink mb-2">Your Projects</h1>
              <p className="text-gray-500">Manage your library of masterpieces.</p>
           </div>

           {appState.projects.length === 0 ? (
             <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-16 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-400">
                  <Icons.Folder className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-700 mb-2">No projects yet</h3>
                <p className="text-gray-500 mb-6 max-w-md">Start your journey by creating your first novel, short story, or screenplay.</p>
                <button 
                  onClick={() => setAppState(prev => ({ ...prev, viewMode: 'setup' }))}
                  className="text-indigo-600 font-bold hover:underline"
                >
                  Create a Project
                </button>
             </div>
           ) : (
             <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {appState.projects.map(project => (
                  <div 
                    key={project.id}
                    onClick={() => handleSelectProject(project.id)}
                    className="group bg-white rounded-2xl border border-gray-200 p-6 shadow-sm hover:shadow-xl hover:border-indigo-200 transition-all cursor-pointer relative overflow-hidden"
                  >
                     <div className="flex justify-between items-start mb-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-amber-100 to-orange-100 text-amber-600 rounded-lg flex items-center justify-center">
                           <Icons.Book />
                        </div>
                        <button 
                           onClick={(e) => handleDeleteProject(project.id, e)}
                           className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                           title="Delete Project"
                        >
                           <Icons.Trash className="w-4 h-4" />
                        </button>
                     </div>
                     <h3 className="text-xl font-serif font-bold text-ink mb-1 group-hover:text-indigo-600 transition-colors line-clamp-1">{project.title}</h3>
                     <div className="flex gap-2 mb-4 text-xs font-medium text-gray-500">
                        <span className="bg-gray-100 px-2 py-0.5 rounded">{project.genre}</span>
                        <span>•</span>
                        <span>{new Date(project.lastModified).toLocaleDateString()}</span>
                     </div>
                     <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed mb-4">
                       {project.synopsis}
                     </p>
                     
                     <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                        <div 
                          className="bg-indigo-500 h-full rounded-full transition-all duration-1000" 
                          style={{ width: `${(project.outline.filter(c => c.isDrafted).length / Math.max(project.outline.length, 1)) * 100}%` }} 
                        />
                     </div>
                     <div className="flex justify-between mt-2 text-xs text-gray-400">
                        <span>{project.outline.filter(c => c.isDrafted).length} / {project.outline.length} Chapters</span>
                        <span>{(project.outline.filter(c => c.isDrafted).length / Math.max(project.outline.length, 1) * 100).toFixed(0)}%</span>
                     </div>
                  </div>
                ))}
             </div>
           )}
        </main>
        {notification && <NotificationToast message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      </div>
    );
  }

  // View Routing
  if (currentProject && appState.viewMode === 'dashboard') {
    return (
      <>
        <Dashboard 
          state={{...appState, project: currentProject}} 
          onStateChange={(updates) => {
             // Handle Project Updates specifically
             if (updates.project) {
                updateCurrentProject(updates.project);
             }
             // Handle View Changes
             if (updates.viewMode) {
                setAppState(prev => ({ ...prev, viewMode: updates.viewMode! }));
             }
             if (updates.activeChapterId) {
                setAppState(prev => ({ ...prev, activeChapterId: updates.activeChapterId! }));
             }
             if (updates.activeTab) {
                setAppState(prev => ({ ...prev, activeTab: updates.activeTab! }));
             }
             if (updates.themeMode) {
                setAppState(prev => ({ ...prev, themeMode: updates.themeMode! }));
             }
          }}
          onLogout={handleLogout}
        />
        {notification && <NotificationToast message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      </>
    );
  }

  if (appState.viewMode === 'setup') {
    return (
      <>
        <ProjectWizard 
          onComplete={handleCreateProject}
          onError={handleError}
          onCancel={() => setAppState(prev => ({ ...prev, viewMode: 'projects' }))}
        />
        {notification && <NotificationToast message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
      </>
    );
  }

  return (
    <>
      <LandingPage onEnter={handleStart} />
      {notification && <NotificationToast message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
    </>
  );
};

export default App;